import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import type { SessionUser } from 'src/shared/session';

import { useCurrentUser } from 'src/app/shared/hooks/session';
import { setPxUserData } from 'src/app/shared/utils/payments';

import { useLoginModal } from './useLoginModal';

type Props = { email: string; showContinueAsGuest: boolean; googleAuthEnabled: boolean; isSunriseEnabled: boolean };

export function useLoginWithModal(): (
	props?: Props,
) => Promise<{ user: SessionUser | undefined; continueAsGuest?: boolean } | undefined> {
	const user = useCurrentUser();
	const track = useTracking();

	const openLoginModal = useLoginModal();

	return useCallback(
		async (
			{ email, showContinueAsGuest, googleAuthEnabled, isSunriseEnabled } = {
				email: '',
				showContinueAsGuest: true,
				googleAuthEnabled: false,
				isSunriseEnabled: false,
			},
		) => {
			if (!user && !email) return undefined;
			const currentEmail = user?.email ?? email;
			void track('login_or_join_view', { context: 'log_in', user_flow: 'payment' });
			const modalResult = await openLoginModal({
				user,
				email: currentEmail,
				showContinueAsGuest,
				googleAuthEnabled,
				isSunriseEnabled,
			}).waitForResult();
			if (!modalResult?.user) {
				void track('close_login_signup', {
					click_location: modalResult?.continueAsGuest ? 'continue_as_guest_click' : 'modal_close',
				});
			}
			setPxUserData(modalResult?.user);
			return modalResult;
		},
		[openLoginModal, user, track],
	);
}
